/**
 * Memoria de preferencias del asistente (como las "memorias" de otros asistentes, pero
 * explícita): lo que la persona pide recordar sobre cómo organiza su tiempo. Se guarda solo en
 * el dispositivo (ver apps/web/src/assistant/store.ts), se puede ver y borrar en Ajustes, y el
 * planificador la usa como punto de partida, explicándolo en cada propuesta.
 */
import { z } from 'zod';
import type { PreferenceChange } from './intents';
import { activityKey } from './planner';

export type { PreferenceChange };
import { Phrase, simplify, takeTime, WEEKDAY, weekdayNumber } from './normalize';

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const day = z.number().int().min(1).max(7);
const slot = z.enum(['morning', 'afternoon', 'evening']);

export const preferencesSchema = z.object({
  /** Nada antes de esta hora. */
  earliest: hhmm.nullable().default(null),
  /** Nada después de esta hora. */
  latest: hhmm.nullable().default(null),
  /** Franja preferida por actividad (clave de activityKey) y su nombre tal como se dijo. */
  slots: z.record(z.object({ slot, label: z.string().max(80) })).default({}),
  /** Días que se prefieren libres. */
  freeDays: z.array(day).max(6).default([]),
});
export type Preferences = z.infer<typeof preferencesSchema>;

export const emptyPreferences = (): Preferences => ({ earliest: null, latest: null, slots: {}, freeDays: [] });


const SLOT_WORDS: [RegExp, 'morning' | 'afternoon' | 'evening'][] = [
  [/\b(?:por la |a la |en la )?manana(?:s)?\b|\btemprano\b/, 'morning'],
  [/\b(?:por la |a la |en la )?tarde(?:s)?\b|\bdespues de comer\b/, 'afternoon'],
  [/\b(?:por la |a la |en la )?noche(?:s)?\b/, 'evening'],
];

const SLOT_ANY = new RegExp(SLOT_WORDS.map(([re]) => re.source).join('|'), 'g');

/**
 * Preferencias en una frase. Requiere un indicio de hábito ("prefiero", "siempre", "nunca",
 * "no me pongas nada…", "los domingos quiero descansar"): sin él, una frase como "déjame libre
 * el domingo" es una petición puntual, no una preferencia.
 */
export function parsePreference(text: string): PreferenceChange | null {
  const n = simplify(text);
  const time = (re: RegExp) => {
    const m = re.exec(n);
    if (!m) return null;
    const t = takeTime(new Phrase(`a las ${m[1]}`));
    return t.type === 'time' ? t.time : t.type === 'ambiguous' ? t.options[n.includes('noche') || n.includes('tarde') ? 1 : 0] : null;
  };
  const earliest = time(/\b(?:no me pongas nada|nada|no quiero nada|nunca nada|no)\s+antes de las?\s+([\w:.]+(?:\s+(?:de la (?:manana|tarde|noche)|am|pm))?)/);
  if (earliest) return { kind: 'earliest', time: earliest };
  const latest = time(/\b(?:no me pongas nada|nada|no quiero nada|nunca nada|no)\s+(?:despues de|mas tarde de)\s+las?\s+([\w:.]+(?:\s+(?:de la (?:manana|tarde|noche)|am|pm))?)/);
  if (latest) return { kind: 'latest', time: latest };
  const pref = /^(?:yo )?(?:prefiero|me gusta mas|siempre|normalmente|me viene mejor)\s+(.+)$/.exec(n);
  if (pref) {
    const rest = pref[1]!;
    const s = SLOT_WORDS.find(([re]) => re.test(rest));
    if (s) {
      const activity = rest.replace(SLOT_ANY, ' ').replace(/\s+/g, ' ').trim();
      if (activity) return { kind: 'slot', activity: activity.slice(0, 80), slot: s[1] };
    }
  }
  const free = new RegExp(`\\b(?:los|todos los)\\s+${WEEKDAY}(?:\\s+(?:y|e)\\s+(?:los\\s+)?${WEEKDAY})?\\s+(?:quiero |prefiero |suelo )?(?:descansar|libres?|no hago nada|no quiero nada|son para descansar|de descanso)\\b`).exec(n);
  if (free) return { kind: 'freeDays', days: [weekdayNumber(free[1]!), ...(free[2] ? [weekdayNumber(free[2])] : [])].sort((a, b) => a - b) };
  return null;
}

export function applyPreference(p: Preferences, c: PreferenceChange): Preferences {
  switch (c.kind) {
    case 'earliest':
      return { ...p, earliest: c.time };
    case 'latest':
      return { ...p, latest: c.time };
    case 'slot':
      return { ...p, slots: { ...p.slots, [activityKey(c.activity)]: { slot: c.slot, label: c.activity } } };
    case 'freeDays':
      return { ...p, freeDays: [...new Set([...p.freeDays, ...c.days])].sort((a, b) => a - b) };
  }
}

/** Franja preferida para una actividad, si se dijo. */
export function preferredSlot(p: Preferences | undefined, activity: string): 'morning' | 'afternoon' | 'evening' | null {
  return p?.slots[activityKey(activity)]?.slot ?? null;
}

/** ¿La hora cae dentro de los límites preferidos? */
export function withinLimits(p: Preferences | undefined, time: string, minutes = 0): boolean {
  if (!p) return true;
  const [h, m] = time.split(':').map(Number);
  const start = h! * 60 + m!;
  if (p.earliest && time < p.earliest) return false;
  if (p.latest) {
    const [lh, lm] = p.latest.split(':').map(Number);
    if (start + minutes > lh! * 60 + lm!) return false;
  }
  return true;
}

/** Frase legible de una preferencia (para confirmarla, listarla y en Ajustes). */
export function describePreference(c: PreferenceChange, t: (k: string, params?: Record<string, string | number>) => string): string {
  switch (c.kind) {
    case 'earliest':
      return t('chat.prefs.describe.earliest', { time: c.time });
    case 'latest':
      return t('chat.prefs.describe.latest', { time: c.time });
    case 'slot':
      return t('chat.prefs.describe.slot', { activity: c.activity, slot: t(`chat.prefs.slots.${c.slot}`) });
    case 'freeDays':
      return t('chat.prefs.describe.freeDays', { days: c.days.map((d) => t(`chat.weekday.${d}`)).join(', ') });
  }
}
